// Pure evaluator for Inbox Composite definitions (ADR 0029).
//
// Takes a parsed `CompositeDefinition`, a conversation, and a small
// context bag of lookup functions, and returns whether the
// conversation matches the filter. Extracted as its own module so
// the evaluator is trivially unit-testable without pulling Shell
// state into the tests.
//
// Design decisions:
//   - `EvaluationContext` holds only *lookup* functions, not whole
//     datasets. Caller supplies however it wants to memoise access
//     (React Query cache snapshot, in-memory Map, etc.).
//   - An empty `predicates` array evaluates to TRUE — an empty
//     filter matches everything. This keeps "filter just created
//     with no predicates yet" from nuking the user's list.
//   - Unknown `kind` from a newer client version would've been
//     stripped at parse time (see compositeSchema.ts). If it ever
//     reaches here despite that, we bias toward `false` in an `or`
//     (ignore the predicate) and `true` in an `and` (ignore the
//     predicate) so the rest of the filter still drives behaviour.

import type { Conversation } from './db'
import type { CompositeDefinition, Predicate } from './compositeSchema'

export interface EvaluationContext {
  /** Set of conversation URNs tagged with a given tagId. */
  getTagsForConversation: (conversationUrn: string) => string[]
  /** Set of starred/bookmarked URNs. */
  isStarred: (conversationUrn: string) => boolean
}

export function evaluatePredicate(
  predicate: Predicate,
  conv: Conversation,
  ctx: EvaluationContext,
): boolean {
  switch (predicate.kind) {
    case 'tag': {
      if (predicate.tagIds.length === 0) return true
      const assigned = ctx.getTagsForConversation(conv.conversationUrn)
      if (predicate.mode === 'all') {
        return predicate.tagIds.every((id) => assigned.includes(id))
      }
      return predicate.tagIds.some((id) => assigned.includes(id))
    }

    case 'inboxView': {
      if (predicate.view === 'ALL') return true
      if (predicate.view === 'UNREAD') return !conv.isRead
      if (predicate.view === 'WAITING_THEIRS') return conv.lastMessageFromMe === true
      if (predicate.view === 'WAITING_MINE') return conv.lastMessageFromMe === false
      // Exhaustiveness guard — unreachable under Zod-validated input.
      return true
    }

    case 'unread':
      return predicate.value ? !conv.isRead : conv.isRead

    case 'starred':
      return predicate.value
        ? ctx.isStarred(conv.conversationUrn)
        : !ctx.isStarred(conv.conversationUrn)

    case 'category':
      return conv.category === predicate.category
  }
}

export function evaluateComposite(
  def: CompositeDefinition,
  conv: Conversation,
  ctx: EvaluationContext,
): boolean {
  if (def.predicates.length === 0) return true
  if (def.combinator === 'and') {
    return def.predicates.every((p) => evaluatePredicate(p, conv, ctx))
  }
  return def.predicates.some((p) => evaluatePredicate(p, conv, ctx))
}
